import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef } from "react";

import { IS_DEV } from "app/env";

/**
 * Should be used for direct user interactions, or low-frequency state transitions within
 * the context of a user's session. Only supports large (minute) rollup windows. If you're
 * trying to tracking high frequency events, or events not related to user actions, use
 * `trackAnalytic` instead.
 *
 * @param name event name
 * @param payload extra information for the event
 * @param useDatadog default true, turn off if you don't want to log to datadog
 */
export function trackUserAction(name: string, payload?: object, useDatadog = true) {
  try {
    if (IS_DEV) {
      console.debug("tracking event", { name, event: payload });
    } else {
      mixpanel.track(name, payload);
      if (useDatadog) {
        datadogRum.addAction(name, payload);
      }
    }
  } catch (e) {
    console.error(e);
    if (IS_DEV) {
      // In dev, throw the error so we are aware there's an issue
      throw e;
    }
  }
}

/**
 * Should be used for high frequency analytics like periodic stats collection. Supports small
 * (second) rollup windows. If you're trying to track events related to user actions, use
 * `trackUserAction` instead.
 *
 * @param log text content
 * @param payload extra information for the log
 * @param useDatadog default true, turn off if you don't want to log to datadog
 */
export function trackAnalytic(log: string, payload?: object, useDatadog = true) {
  try {
    if (IS_DEV) {
      console.debug("tracking log event", { log, event: payload });
    } else {
      mixpanel.track(log, payload);
      if (useDatadog) {
        datadogLogs.logger.info(log, payload);
      }
    }
  } catch (e) {
    console.error(e);
    if (IS_DEV) {
      // In dev, throw the error so we are aware there's an issue
      throw e;
    }
  }
}

/**
 * Hook that tracks an event when a condition becomes true
 */
export function useTrackUserAction(condition: boolean, name: string, payload?: object) {
  const previousConditionRef = useRef(false);
  // Effect to track event when the condition becomes true
  useEffect(() => {
    if (condition && previousConditionRef.current === false) {
      trackUserAction(name, payload);
      previousConditionRef.current = true;
      return;
    }
    if (!condition && previousConditionRef.current === true) {
      previousConditionRef.current = false;
      return;
    }
  }, [condition, name, payload]);
}
